import {
  CSSProperties,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, BUTTON_COLORS } from "./Button";
import { breakPoints } from "../../breakPoints";
import { AllowedResolution } from "../../../types/resize";
import { URLS } from "../config";

export type ImageSizeSetting = {
  width?: string | number;
  height?: string | number;
  maxHeight?: string | number;
  maxWidth?: string | number;
  z: number;
  top?: number | string;
  left?: number | string;
  transformX?: number | string;
  transformY?: number | string;
  imagePath?: string;
};
export type CornerActionType = {
  onAction?: () => void;
  actionIcon?: JSX.Element | string;
  actionColor?: keyof typeof BUTTON_COLORS;
  actionTitle?: string;
  actionSize?: number | string;
  actionCorner?: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
};
export type ImagePreviewProps = {
  image?: { path?: string; ref?: string; name?: string };
  size?: ImageSizeSetting;
  hoverSize?: ImageSizeSetting;
  onClick?: MouseEventHandler<HTMLDivElement>;
  rounded?: boolean;
  alt: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  absolute?: boolean;
  cursor?: CSSProperties["cursor"];
  imageRef?: MutableRefObject<HTMLImageElement | null>;
  transition?: "none" | 300;
  containerClass?: string;
  useImageSize?: AllowedResolution;
  objectFit?: "cover" | "contain" | "fill" | "none";
  preload?: boolean;
} & CornerActionType;
const AbsoluteWrapper = ({
  children,
  absolute,
  size,
  currSize,
  alt,
  imagePath,
  transition,
  useImageSize,
  objectFit,
}: Pick<
  ImagePreviewProps,
  "absolute" | "alt" | "transition" | "useImageSize" | "objectFit"
> & {
  children: PropsWithChildren["children"];
  size: ImageSizeSetting;
  currSize: ImageSizeSetting;
  imagePath: string;
}) => {
  return (
    <>
      {absolute ? (
        <>
          <img
            className={"invisible"}
            alt={alt}
            style={{
              height: size.height,
              width: size.width,
              maxHeight: size.maxHeight,
              maxWidth: size.maxWidth,
              objectFit,
            }}
            height={size.height}
            width={size.width}
            src={URLS.image(imagePath, { useImageSize })}
            {...(useImageSize ? {} : generateSrcSetAndSizes(imagePath))}
          />
          <div
            className={"absolute " + (transition === 300 ? "duration-300" : "")}
            style={{
              top: currSize.top ?? "0",
              left: currSize.left ?? "0",
              transform:
                "translate(" +
                (currSize.transformX ?? "0") +
                ", " +
                (currSize.transformY ?? "0") +
                ")",
            }}
          >
            <img
              className={
                "invisible " + (transition === 300 ? "duration-300" : "")
              }
              alt={alt}
              style={{
                height: currSize.height,
                width: currSize.width,
                maxHeight: currSize.maxHeight,
                maxWidth: currSize.maxWidth,
                objectFit,
              }}
              height={currSize.height}
              width={currSize.width}
              src={URLS.image(currSize.imagePath || imagePath, {
                useImageSize,
              })}
              {...(useImageSize ? {} : generateSrcSetAndSizes(imagePath))}
            />
            {children}
          </div>
        </>
      ) : (
        children ?? null
      )}
    </>
  );
};
export const ImagePreview = ({
  image,
  size = { height: 200, z: 1 },
  hoverSize = size,
  onMouseEnter,
  onMouseLeave,
  onAction,
  actionIcon = "X",
  actionColor = "warning",
  actionTitle,
  actionSize = 36,
  actionCorner = "topLeft",
  rounded,
  onClick,
  alt,
  absolute,
  cursor = "",
  imageRef,
  transition = 300,
  containerClass,
  useImageSize,
  objectFit = "cover",
  preload,
}: ImagePreviewProps) => {
  const [currSize, setCurrSize] = useState(size);
  useEffect(() => {
    setCurrSize(size);
  }, [size]);
  const touchEventsSeen = useRef(false);
  const checkIfNotWideEnoughForHover = useCallback(() => {
    return window?.innerWidth < 700;
  }, []);

  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();
  return image?.path ? (
    <>
      {/* <Head>
        {preload ? (
          <link
            rel="preload"
            href={URLS.image(image.path, {
              useImageSize,
            })}
            key={image.path}
            as="image"
            // fetchPriority="low"
            imageSrcSet={generateSrcSetAndSizes(image.path).srcSet}
            // "wolf_400px.jpg 400w, wolf_800px.jpg 800w, wolf_1600px.jpg 1600w"
            // imageSizes="100vw"
            imageSizes={generateSrcSetAndSizes(image.path).sizes}
          />
        ) : null}
      </Head> */}
      <div
        className={
          "relative " +
          (transition === 300 ? " duration-300 " : " ") +
          containerClass
        }
        style={{
          cursor,
          height: size.height,
          width: size.width,
          maxHeight: size.maxHeight,
          maxWidth: size.maxWidth,
          zIndex: currSize.z, // placed here so the icon can stay above the image as the user changes z index
        }}
        onTouchStartCapture={() => {
          touchEventsSeen.current = true;
        }}
        onMouseEnter={() => {
          hoverTimeout.current && clearTimeout(hoverTimeout.current);
          //Not a touch event
          if (!touchEventsSeen.current && !checkIfNotWideEnoughForHover()) {
            setCurrSize(hoverSize);
            onMouseEnter?.();
          }
        }}
        onMouseLeave={() => {
          setCurrSize((s) => ({
            ...s,
            z: Math.floor((hoverSize.z - size.z) / 2 + size.z),
          }));
          hoverTimeout.current = setTimeout(() => {
            setCurrSize(size);
            onMouseLeave?.();
          }, 150);
        }}
      >
        <AbsoluteWrapper
          absolute={absolute}
          currSize={currSize}
          size={size}
          imagePath={image.path}
          alt={alt}
          transition={transition}
          useImageSize={useImageSize}
          objectFit={objectFit}
        >
          {onAction ? (
            <Button
              btnColor={actionColor}
              btnType="small"
              title={actionTitle}
              onClick={(evt) => {
                evt.stopPropagation();
                evt.preventDefault();
                onAction();
              }}
              className="absolute !p-0 z-10 "
              style={{
                top:
                  actionCorner === "topLeft" || actionCorner === "topRight"
                    ? 5
                    : undefined,
                bottom:
                  actionCorner === "bottomLeft" ||
                  actionCorner === "bottomRight"
                    ? 5
                    : undefined,
                right:
                  actionCorner === "topRight" || actionCorner === "bottomRight"
                    ? 5
                    : undefined,
                left:
                  actionCorner === "topLeft" || actionCorner === "bottomLeft"
                    ? 5
                    : undefined,
                height: actionSize,
                width: actionSize,
              }}
            >
              {actionIcon}
            </Button>
          ) : null}
          <div
            onClick={onClick}
            className={
              (absolute ? " absolute " : "") +
              " inset-0 shadow-lg hover:shadow-2xl " +
              (transition === 300 ? " duration-300 " : "")
            }
            style={{
              height: currSize.height,
              width: currSize.width,
              maxHeight: currSize.maxHeight,
              maxWidth: currSize.maxWidth,
              objectFit,
            }}
          >
            <img
              ref={imageRef}
              className={
                (transition === 300 ? "duration-300" : "") +
                " " +
                (rounded ? "rounded" : "")
              }
              alt={alt}
              style={{
                height: currSize.height,
                width: currSize.width,
                maxHeight: currSize.maxHeight,
                maxWidth: currSize.maxWidth,
                objectFit,
              }}
              height={currSize.height}
              width={currSize.width}
              src={URLS.image(currSize.imagePath || image.path, {
                useImageSize,
              })}
              {...(useImageSize
                ? {}
                : generateSrcSetAndSizes(currSize.imagePath || image.path))}
            />
            {hoverSize.imagePath ? (
              <img
                className={"aboslute hidden"}
                fetchPriority={preload ? "high" : "low"}
                alt={alt}
                style={{
                  height: currSize.height,
                  width: currSize.width,
                  maxHeight: currSize.maxHeight,
                  maxWidth: currSize.maxWidth,
                  objectFit,
                }}
                src={URLS.image(hoverSize.imagePath, { useImageSize })}
                {...(useImageSize
                  ? {}
                  : generateSrcSetAndSizes(hoverSize.imagePath))}
              />
            ) : null}
          </div>
        </AbsoluteWrapper>
      </div>
    </>
  ) : (
    <div>No Image Available</div>
  );
};

const generateSrcSetAndSizes = (imagePath: string) => {
  return {
    srcSet: `${import.meta.env.VITE_SITE_URL}/images/size-xs/${imagePath} 200w,
${import.meta.env.VITE_SITE_URL}/images/size-sm/${imagePath} 340w,
${import.meta.env.VITE_SITE_URL}/images/size-md/${imagePath} 680w,
${import.meta.env.VITE_SITE_URL}/images/size-lg/${imagePath} 724w,
${import.meta.env.VITE_SITE_URL}/images/size-xl/${imagePath} 900w,
${import.meta.env.VITE_SITE_URL}/images/size-2xl/${imagePath} 1080w,
${import.meta.env.VITE_SITE_URL}/images/size-3xl/${imagePath} 1236w,
${import.meta.env.VITE_SITE_URL}/images/${imagePath} 1400w,
${import.meta.env.VITE_SITE_URL}/images/size-h300/${imagePath} 300h,
${import.meta.env.VITE_SITE_URL}/images/size-h500/${imagePath} 500h,`,
    sizes: `(max-width: ${breakPoints.xl - 1}px) 70vw, 40vw`,
  };
};
